import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CircleCheck } from "lucide-react";
import { api } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) return setError("Password must be at least 6 characters.");
    if (pwd !== confirm) return setError("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16 flex items-center justify-center px-5 bg-[#0F0F10]">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md border border-white/10 bg-[#1F1F1F] p-8 sm:p-10"
        data-testid="reset-password-page"
      >
        <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#4ADE80] mb-4">Account</p>
        <h1 className="font-display font-black text-5xl tracking-tighter uppercase leading-none">New Password</h1>

        {!token ? (
          <p data-testid="reset-invalid" className="mt-8 border border-[#EF4444]/40 bg-[#EF4444]/10 text-[#EF4444] font-mono-accent text-xs px-4 py-3">
            This reset link is invalid. Please request a new one from <Link to="/forgot-password" className="underline">forgot password</Link>.
          </p>
        ) : done ? (
          <div data-testid="reset-success" className="mt-8 flex items-center gap-3 border border-[#4ADE80]/40 bg-[#4ADE80]/10 px-4 py-4">
            <CircleCheck className="h-5 w-5 text-[#4ADE80] shrink-0" />
            <p className="text-[#F5F5F5] text-sm leading-relaxed">Password reset. Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <p className="mt-4 text-[#A8A8A8] text-sm">Choose a new password for your account.</p>
            {error && (
              <p data-testid="reset-error" className="mt-6 border border-[#EF4444]/40 bg-[#EF4444]/10 text-[#EF4444] font-mono-accent text-xs px-4 py-3">{error}</p>
            )}
            <form onSubmit={submit} className="mt-8 space-y-4">
              <input data-testid="reset-password" required type="password" placeholder="New password (min 6 chars)" value={pwd} onChange={(e) => setPwd(e.target.value)} className="w-full bg-[#0F0F10] border border-white/10 px-4 py-4 font-mono-accent text-sm outline-none focus:border-[#4ADE80] transition-colors placeholder:text-[#7a7a7a]" />
              <input data-testid="reset-confirm" required type="password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full bg-[#0F0F10] border border-white/10 px-4 py-4 font-mono-accent text-sm outline-none focus:border-[#4ADE80] transition-colors placeholder:text-[#7a7a7a]" />
              <button data-testid="reset-submit" type="submit" disabled={loading} className="btn-press w-full bg-[#4ADE80] text-[#0F0F10] font-medium py-4 hover:bg-[#6FE998] hover:shadow-lg hover:shadow-[#4ADE80]/20 transition-[background-color,box-shadow] flex items-center justify-center gap-2 disabled:opacity-50">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "Resetting…" : "Reset Password"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
